import React, { useEffect, useMemo, useState } from 'react';

import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { Modal, Form, Input, InputNumber, Select, Switch, Button, Space, Typography, Descriptions, Divider, Alert, Row, Col, Spin, message } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text, Title } = Typography;

/**
 * ProductQuickEditDrawer - 快速編輯商品計費相關欄位
 *
 * 用途：在驗收流程中快速補充商品的計費必填欄位
 * - 棧板數量 (total_large_units_per_pallet, small_quantity_per_large_unit)
 * - 材積 (length, width, height)
 * - 重量 (weight_kg)
 *
 * @param {boolean} open - 是否開啟
 * @param {function} onClose - 關閉回調
 * @param {object} product - 商品資料 (需含 id, sku, name)
 * @param {object} productService - 商品服務 (需含 get, update 方法)
 * @param {string} billingMode - 計費模式 (可選，用於高亮需要填寫的欄位)
 * @param {function} onSuccess - 更新成功回調
 */
export default function ProductQuickEditDrawer({
  open,
  onClose,
  product,
  productService,
  billingMode,
  onSuccess
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [productData, setProductData] = useState(null);

  // 載入完整商品資料
  useEffect(() => {
    if (!(open && product?.id && productService)) return;

    setLoading(true);
    productService.get(product.id)
      .then(response => {
        if (response.success) {
          setProductData(response.data);
          const fieldValues = {
            length: response.data.length,
            width: response.data.width,
            height: response.data.height,
            weight_kg: response.data.weight_kg,
            small_quantity_per_large_unit: response.data.small_quantity_per_large_unit,
            total_large_units_per_pallet: response.data.total_large_units_per_pallet,
          };
          // 沒設自訂屬性時不放進 form，避免提交空 hash 觸發 Product#validate_custom_attributes 與 dirty tracking
          if ((response.data.product_attribute_definitions || []).length > 0) {
            fieldValues.custom_attributes = response.data.custom_attributes || {};
          }
          form.setFieldsValue(fieldValues);
        } else {
          handleApiError(response, { t, fallbackMessage: t('common.error.fetch_failed') });
        }
      })
      .catch(err => handleNetworkError(err, { t, fallbackMessage: t('common.error.fetch_failed') }))
      .finally(() => setLoading(false));
  }, [open, product?.id, productService, form, t]);

  // 計算 units_per_pallet
  const smallQtyPerLargeUnit = Form.useWatch('small_quantity_per_large_unit', form);
  const totalLargeUnitsPerPallet = Form.useWatch('total_large_units_per_pallet', form);
  const unitsPerPallet = (smallQtyPerLargeUnit || 0) * (totalLargeUnitsPerPallet || 0);

  // 計算 CBM
  const length = Form.useWatch('length', form);
  const width = Form.useWatch('width', form);
  const height = Form.useWatch('height', form);
  const cbm = (length > 0 && width > 0 && height > 0)
    ? ((length * width * height) / 1000000).toFixed(5)
    : 0;

  const weightKg = Form.useWatch('weight_kg', form);

  // 判斷哪些欄位需要填寫
  const needsPallet = ['by_sku_pallet', 'by_total_pallet'].includes(billingMode);
  const needsDimensions = billingMode === 'by_exact_volume';
  const needsWeight = billingMode === 'by_weight';

  const productAttributeDefinitions = useMemo(
    () => productData?.product_attribute_definitions || [],
    [productData]
  );

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // 對應載入時的條件，沒自訂屬性就不送 custom_attributes，避免後端被迫處理空 hash
      const payload = { ...values };
      if (productAttributeDefinitions.length === 0) {
        delete payload.custom_attributes;
      }

      const response = await productService.update(product.id, payload);

      if (response.success) {
        message.success(t('common.message.update_success'));
        onSuccess?.(response.data);
        onClose();
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.update_failed') });
      }
    } catch (error) {
      // 用 Array.isArray 而非 truthy — 避免帶有 errorFields 屬性的真錯誤被靜默吞掉
      if (Array.isArray(error?.errorFields)) return;
      handleNetworkError(error, { t, fallbackMessage: t('common.error.update_failed') });
    } finally {
      setSaving(false);
    }
  };

  const renderMissingAlert = () => {
    const missing = [];

    if (needsPallet && unitsPerPallet <= 0) {
      missing.push(t('shared.products.quick_edit.missing_pallet'));
    }
    if (needsDimensions && cbm <= 0) {
      missing.push(t('shared.products.quick_edit.missing_dimensions'));
    }
    if (needsWeight && (!weightKg || weightKg <= 0)) {
      missing.push(t('shared.products.quick_edit.missing_weight'));
    }

    if (missing.length === 0) return null;

    return (
      <Alert
        type="warning"
        showIcon
        message={t('shared.products.quick_edit.missing_fields_title')}
        description={
          <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
            {missing.map((item, idx) => <li key={idx}>{item}</li>)}
          </ul>
        }
        style={{ marginBottom: 16 }}
      />
    );
  };

  return (
    <Modal
      title={t('shared.products.quick_edit.title')}
      width={600}
      onCancel={onClose}
      open={open}
      footer={
        <Space>
          <Button onClick={onClose}>{t('common.button.cancel')}</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            {t('common.button.save')}
          </Button>
        </Space>
      }
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* 商品基本資訊 */}
          <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label={t('models.product.sku')}>
              <Text strong copyable>{product?.sku || productData?.sku}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.name')}>
              {product?.name || productData?.name}
            </Descriptions.Item>
          </Descriptions>

          {renderMissingAlert()}

          <Form form={form} layout="vertical">
            {/* 棧板相關 */}
            <div style={{
              padding: 16,
              background: needsPallet ? '#fffbe6' : '#fafafa',
              borderRadius: 8,
              marginBottom: 16,
              border: needsPallet ? '1px solid #ffe58f' : '1px solid #f0f0f0'
            }}>
              <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
                {t('shared.products.quick_edit.pallet_section')}
                {needsPallet && <Text type="danger" style={{ marginLeft: 8, fontSize: 12 }}>*</Text>}
              </Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={t('models.product.small_quantity_per_large_unit')}
                    name="small_quantity_per_large_unit"
                    tooltip={t('shared.products.quick_edit.small_quantity_per_large_unit_tip')}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 24" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t('models.product.total_large_units_per_pallet')}
                    name="total_large_units_per_pallet"
                    tooltip={t('shared.products.quick_edit.total_large_units_per_pallet_tip')}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 48" />
                  </Form.Item>
                </Col>
              </Row>
              <div style={{ textAlign: 'right' }}>
                <Text type="secondary">{t('shared.products.quick_edit.units_per_pallet')}: </Text>
                <Text strong style={{ fontSize: 16, color: unitsPerPallet > 0 ? '#52c41a' : '#ff4d4f' }}>
                  {unitsPerPallet}
                </Text>
              </div>
            </div>

            {/* 材積相關 */}
            <div style={{
              padding: 16,
              background: needsDimensions ? '#fffbe6' : '#fafafa',
              borderRadius: 8,
              marginBottom: 16,
              border: needsDimensions ? '1px solid #ffe58f' : '1px solid #f0f0f0'
            }}>
              <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
                {t('shared.products.quick_edit.dimensions_section')}
                {needsDimensions && <Text type="danger" style={{ marginLeft: 8, fontSize: 12 }}>*</Text>}
              </Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label={t('models.product.length')} name="length">
                    <InputNumber min={0} style={{ width: '100%' }} addonAfter="cm" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={t('models.product.width')} name="width">
                    <InputNumber min={0} style={{ width: '100%' }} addonAfter="cm" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={t('models.product.height')} name="height">
                    <InputNumber min={0} style={{ width: '100%' }} addonAfter="cm" />
                  </Form.Item>
                </Col>
              </Row>
              <div style={{ textAlign: 'right' }}>
                <Text type="secondary">CBM: </Text>
                <Text strong style={{ fontSize: 16, color: cbm > 0 ? '#52c41a' : '#ff4d4f' }}>
                  {cbm} m³
                </Text>
              </div>
            </div>

            {/* 重量相關 */}
            <div style={{
              padding: 16,
              background: needsWeight ? '#fffbe6' : '#fafafa',
              borderRadius: 8,
              border: needsWeight ? '1px solid #ffe58f' : '1px solid #f0f0f0'
            }}>
              <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
                {t('shared.products.quick_edit.weight_section')}
                {needsWeight && <Text type="danger" style={{ marginLeft: 8, fontSize: 12 }}>*</Text>}
              </Title>
              <Form.Item label={t('models.product.weight_kg')} name="weight_kg">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} addonAfter="kg" />
              </Form.Item>
            </div>

            {/* 自訂屬性：含已有值與必填皆可在此修改，避免必填驗證直接擋下儲存 */}
            {productAttributeDefinitions.length > 0 && (
              <>
                <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
                  <Text type="secondary">{t('models.product.custom_attributes')}</Text>
                </Divider>
                <Row gutter={[16, 0]}>
                  {productAttributeDefinitions.map((def) => {
                    const fieldName = ['custom_attributes', def.name];
                    const label = t(`models.product_attribute.${def.name}`, { defaultValue: def.label });
                    const isBoolean = def.data_type === 'boolean';
                    let inputComponent;
                    switch (def.data_type) {
                      case 'number':
                        inputComponent = <InputNumber style={{ width: '100%' }} placeholder={label} />;
                        break;
                      case 'date':
                        inputComponent = <Input type="date" placeholder={label} />;
                        break;
                      case 'boolean':
                        // Boolean is always binary; Schema 層 (ProductAttributeDefinition)
                        // 已禁止 boolean + required=true，因此這裡永遠用 Switch。
                        inputComponent = <Switch />;
                        break;
                      case 'select':
                        inputComponent = (
                          <Select
                            placeholder={label}
                            allowClear
                            options={(def.options || []).map((opt) => ({ label: opt, value: opt }))}
                          />
                        );
                        break;
                      default:
                        inputComponent = <Input placeholder={label} />;
                    }
                    return (
                      <Col xs={24} sm={12} md={8} key={def.id}>
                        <Form.Item
                          label={label}
                          name={fieldName}
                          rules={def.required ? [{ required: true, message: t('common.validation.required') }] : []}
                          valuePropName={isBoolean ? 'checked' : 'value'}
                          initialValue={isBoolean ? false : undefined}
                        >
                          {inputComponent}
                        </Form.Item>
                      </Col>
                    );
                  })}
                </Row>
              </>
            )}
          </Form>
        </>
      )}
    </Modal>
  );
}
